import { storage, path } from '@commons';
import { CONFIG_SESSION } from '@handler/common/startSession';

/**
 * Valida se o usuário tem acesso a um módulo específico
 * @param {string} moduleKey - Chave do módulo (ex: 'moduloAgricultura', 'moduloFinanceiro')
 * @returns {boolean} - true se tem acesso ao módulo
 */
export const hasModuleAccess = (moduleKey) => {
  try {
    const configSession = JSON.parse(storage.get(CONFIG_SESSION));
    const sessionUser = JSON.parse(storage.get(path.sessionUser));

    // Perfil SUPORTE_AGRARE tem acesso total
    if (sessionUser?.perfil === 'SUPORTE_AGRARE') {
      return true;
    }

    // Verifica se o módulo está ativo na conta
    return configSession?.contaAgrare?.[moduleKey] === true;
  } catch (error) {
    console.error('Erro ao validar acesso ao módulo:', error);
    return false;
  }
};

/**
 * Valida se o usuário tem uma role específica
 * @param {string} roleEnum - Literal da role (ex: 'VISUALIZAR_PLANEJAMENTO')
 * @returns {boolean} - true se tem a role
 */
export const hasRole = (roleEnum) => {
  try {
    const configSession = JSON.parse(storage.get(CONFIG_SESSION));
    const sessionUser = JSON.parse(storage.get(path.sessionUser));

    // Perfil SUPORTE_AGRARE tem acesso total
    if (sessionUser?.perfil === 'SUPORTE_AGRARE') {
      return true;
    }

    // Perfil ID 1 (administrador) tem acesso total
    if (configSession?.perfilUser?.id === 1) {
      return true;
    }

    // Verifica se o usuário tem a role específica e está ativa
    const roles = configSession?.perfilUser?.roles || [];
    return roles.some(
      role => role.roleEnum?.literal === roleEnum && role.ativo === true
    );
  } catch (error) {
    console.error('Erro ao validar role:', error);
    return false;
  }
};

/**
 * Valida se o usuário tem acesso à propriedade selecionada
 * @returns {boolean} - true se tem acesso à propriedade
 */
export const hasPropertyAccess = () => {
  try {
    const configSession = JSON.parse(storage.get(CONFIG_SESSION));
    const sessionUser = JSON.parse(storage.get(path.sessionUser));

    // Perfil SUPORTE_AGRARE tem acesso total
    if (sessionUser?.perfil === 'SUPORTE_AGRARE') {
      return true;
    }

    // Perfil ID 1 (administrador) tem acesso total
    if (configSession?.perfilUser?.id === 1) {
      return true;
    }

    const perfilPropriedades = configSession?.perfilUser?.perfilPropriedades || [];
    const fazendaSelecionada = configSession?.fazendaSelecionada;

    if (!fazendaSelecionada) {
      return false;
    }

    // Verifica se tem permissão para a propriedade selecionada
    return perfilPropriedades.some(
      pp => pp.propriedade?.id === fazendaSelecionada.id && pp.permiteAcesso === true
    );
  } catch (error) {
    console.error('Erro ao validar acesso à propriedade:', error);
    return false;
  }
};

/**
 * Valida se o usuário tem acesso completo (módulo + role + propriedade)
 * @param {Object} options - Opções de validação
 * @param {string} options.module - Chave do módulo (opcional)
 * @param {string} options.role - Literal da role (opcional)
 * @param {boolean} options.checkProperty - Se deve validar acesso à propriedade (padrão: true)
 * @returns {boolean} - true se tem todos os acessos necessários
 */
export const hasFullAccess = ({ module, role, checkProperty = true }) => {
  try {
    const sessionUser = JSON.parse(storage.get(path.sessionUser));

    // Perfil SUPORTE_AGRARE tem acesso total
    if (sessionUser?.perfil === 'SUPORTE_AGRARE') {
      return true;
    }

    // Valida módulo se fornecido
    if (module && !hasModuleAccess(module)) {
      return false;
    }

    // Valida propriedade se solicitado
    if (checkProperty && !hasPropertyAccess()) {
      return false;
    }

    // Valida role se fornecida
    if (role && !hasRole(role)) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Erro ao validar acesso completo:', error);
    return false;
  }
};

/**
 * Valida se o usuário é administrador
 * @returns {boolean} - true se é administrador
 */
export const isAdmin = () => {
  try {
    const configSession = JSON.parse(storage.get(CONFIG_SESSION));
    const sessionUser = JSON.parse(storage.get(path.sessionUser));

    return (
      sessionUser?.perfil === 'SUPORTE_AGRARE' ||
      configSession?.perfilUser?.id === 1
    );
  } catch (error) {
    console.error('Erro ao validar se é administrador:', error);
    return false;
  }
};

/**
 * Mapeamento de licenças para módulos
 */
export const LICENSE_MODULE_MAP = {
  'MODULO_AGRICULTURA': 'moduloAgricultura',
  'MODULO_FINANCEIRO': 'moduloFinanceiro',
  'MODULO_ESTOQUE': 'moduloEstoque',
  'MODULO_PLUVIOMETRO': 'moduloPluviometro',
  'MODULO_MONITORAMENTO_FISCAL': 'moduloMonitoramentoFiscal',
  'MODULO_NFE': 'moduloNFe',
  'MODULO_OFX': 'moduloOFX',
  'MODULO_MAPA': 'moduloMapa',
  'MODULO_NDVI': 'moduloNdvi',
  'LIC_MOD_CONTROLE_PRAGA': 'moduloControlePragaDoenca',
};

/**
 * Valida acesso por licença (compatibilidade com código existente)
 * @param {string} license - Chave da licença (ex: 'MODULO_AGRICULTURA')
 * @returns {boolean} - true se tem acesso
 */
export const hasLicenseAccess = (license) => {
  // Caso especial: validação de dois módulos
  if (license === 'MODULO_MONITORAMENTO_FISCAL_MODULO_NFE') {
    return hasModuleAccess('moduloMonitoramentoFiscal') || hasModuleAccess('moduloNFe');
  }

  const moduleKey = LICENSE_MODULE_MAP[license];
  return moduleKey ? hasModuleAccess(moduleKey) : false;
};
